import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import PermissionService from 'src/services/permission-service';

// 'device' nunca passa por linkFor/useIntegrationAccess/useSetIntegrationAccess -- o dispositivo
// nao tem tabela de Permission propria (MON-556), continua na coluna shareable/mirrorable direto.
// Existe aqui so pra tipar o prop `owner` de IntegrationAccessToggles no modo controlado
// (value/onChange), que e o unico caminho usado pelo dispositivo.
export type AccessOwner = 'customer' | 'user' | 'device';

export const integrationAccessKeys = {
  all: ['integrationAccess'] as const,
  of: (owner: AccessOwner, ownerId?: number) => [...integrationAccessKeys.all, owner, ownerId] as const,
};

// O endpoint devolve o vínculo com o nome da coluna do banco, em minúsculas.
const toIntegrationId = (row: Record<string, number>) => row.integrationid ?? row.integrationId;

const linkFor = (owner: AccessOwner, ownerId: number, integrationId: number) =>
  owner === 'customer' ?
    {customerId: ownerId, integrationId} :
    {userId: ownerId, integrationId};

/**
 * Ids das integrações liberadas para uma central ou para um usuário.
 */
export const useIntegrationAccess = (owner: AccessOwner, ownerId?: number) => {
  const permissionService = new PermissionService();

  return useQuery<unknown, unknown, number[]>({
    queryKey: integrationAccessKeys.of(owner, ownerId),
    queryFn: () => permissionService.list({
      ownerClass: owner,
      ownerId,
      propertyClass: 'integration',
    }),
    enabled: !!ownerId,
    initialData: [],
    select: (rows: unknown) => (rows as Record<string, number>[]).map(toIntegrationId),
    retry: false,
  });
};

/**
 * O toggle grava na hora: liga cria o vínculo, desliga remove.
 */
export const useSetIntegrationAccess = (owner: AccessOwner) => {
  const queryClient = useQueryClient();
  const permissionService = new PermissionService();

  return useMutation({
    mutationFn: ({ownerId, integrationId, granted}: {
      ownerId: number; integrationId: number; granted: boolean;
    }) => {
      const link = linkFor(owner, ownerId, integrationId);
      return granted ?
        permissionService.addPermission(link) :
        permissionService.removePermission(link);
    },
    onSuccess: (_data, {ownerId}) => queryClient.invalidateQueries({
      queryKey: integrationAccessKeys.of(owner, ownerId),
    }),
  });
};

export const grantIntegrations = (
  owner: AccessOwner, ownerId: number, integrationIds: number[],
) => {
  const permissionService = new PermissionService();
  return Promise.all(integrationIds.map(
    (integrationId) => permissionService.addPermission(linkFor(owner, ownerId, integrationId))));
};
